export const CITY_LIST_PAGE_SIZE = 10;

export interface SearchRoomChild {
  age: number;
}

export interface SearchRoomRequest {
  adults: number;
  children: SearchRoomChild[];
}

export const DEFAULT_SEARCH_ROOMS: SearchRoomRequest[] = [{ adults: 1, children: [] }];

export const DEFAULT_ROOMS_PARAM = JSON.stringify(DEFAULT_SEARCH_ROOMS);

export interface CityFacility {
  id: number;
  featureName: string;
}

export interface CityAccommodation {
  id: number;
  name: string;
  enName: string;
  faCityName: string;
  zoneID: number;
  enCityName: string;
  starRating: number;
  address: string;
  description: string;
  media: { thumbnailURL: string }[];
  minRoomPrice: number;
  discount: number | null;
  cityDescription: string;
  badges?: { badgeId: number; badgeName: string }[];
  contents?: unknown[];
}

export interface CitySearchQueryInput {
  city: string;
  pageNumber: number;
  pageSize?: number;
  rooms?: SearchRoomRequest[];
  checkIn?: string | null;
  checkOut?: string | null;
  name?: string;
  starRatings?: number[];
  accommodationTypes?: string[];
  facilities?: number[];
  meals?: string[];
  userRatings?: string[];
  specialOffer?: boolean;
  minPrice?: string;
  maxPrice?: string;
}

function decodeRepeated(value: string): string {
  let current = value.trim();
  for (let i = 0; i < 2; i += 1) {
    try {
      const decoded = decodeURIComponent(current);
      if (decoded === current) break;
      current = decoded;
    } catch {
      break;
    }
  }
  return current;
}

export function normalizeCityForSearch(cityName: string): string {
  const decoded = decodeRepeated(cityName);
  if (!decoded) return '';
  if (decoded.toLowerCase() === 'hotels') return 'hotels';
  return decoded.toUpperCase();
}

function tryParseJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function normalizeRoom(entry: unknown): SearchRoomRequest | null {
  if (!entry || typeof entry !== 'object') return null;
  const rec = entry as Record<string, unknown>;
  const adults = Number(rec.adults);
  if (!Number.isInteger(adults) || adults < 1) return null;

  const childrenRaw = rec.children;
  const children: SearchRoomChild[] = [];
  if (Array.isArray(childrenRaw)) {
    for (const child of childrenRaw) {
      if (typeof child === 'number' && Number.isFinite(child)) {
        children.push({ age: Math.max(0, Math.floor(child)) });
        continue;
      }
      if (child && typeof child === 'object' && 'age' in child) {
        const age = Number((child as { age: unknown }).age);
        children.push({ age: Number.isFinite(age) ? Math.max(0, Math.floor(age)) : 0 });
      }
    }
  }

  return { adults, children };
}

export function parseRoomsQueryOrNull(
  raw: string | string[] | null | undefined,
): SearchRoomRequest[] | null {
  if (raw == null) return null;
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (typeof value !== 'string' || !value.trim()) return null;

  let trimmed = value.trim();
  if (trimmed.startsWith('=')) trimmed = trimmed.slice(1).trim();

  const parsed =
    tryParseJson(trimmed) ??
    tryParseJson(decodeRepeated(trimmed));

  if (!Array.isArray(parsed) || parsed.length === 0) {
    return null;
  }

  const rooms = parsed
    .map(normalizeRoom)
    .filter((room): room is SearchRoomRequest => room != null);

  return rooms.length > 0 ? rooms : null;
}

export function parseRoomsQuery(raw: string | string[] | null | undefined): SearchRoomRequest[] {
  return parseRoomsQueryOrNull(raw) ?? DEFAULT_SEARCH_ROOMS;
}

export function roomsToQueryValue(rooms: SearchRoomRequest[]): string {
  return JSON.stringify(rooms);
}

export function buildAccommodationsSearchParams(
  input: CitySearchQueryInput,
): Record<string, string | number | boolean | string[] | number[]> {
  const rooms = input.rooms?.length ? input.rooms : DEFAULT_SEARCH_ROOMS;
  const params: Record<string, string | number | boolean | string[] | number[]> = {
    City: normalizeCityForSearch(input.city),
    PageNumber: input.pageNumber,
    PageSize: input.pageSize ?? CITY_LIST_PAGE_SIZE,
    rooms: roomsToQueryValue(rooms),
  };

  if (input.checkIn) params.StartDate = input.checkIn;
  if (input.checkOut) params.EndDate = input.checkOut;
  if (input.name) params.Name = input.name;
  if (input.starRatings?.length) params.StarRating = input.starRatings;
  if (input.accommodationTypes?.length) params.Type = input.accommodationTypes;
  if (input.facilities?.length) params.FeatureIds = input.facilities;
  if (input.meals?.length) params.Meal = input.meals;
  if (input.userRatings?.length) params.UserRating = input.userRatings;
  if (input.specialOffer) params.IsSpecialOffer = true;
  if (input.minPrice) params.MinPrice = input.minPrice;
  if (input.maxPrice) params.MaxPrice = input.maxPrice;

  return params;
}

export function hasMoreSearchResults(
  loadedCount: number,
  pageItemCount: number,
  totalCount: number,
  pageSize: number = CITY_LIST_PAGE_SIZE,
): boolean {
  if (pageItemCount <= 0) return false;
  if (pageItemCount < pageSize) return false;
  if (totalCount > 0) return loadedCount < totalCount;
  return true;
}

export function extractSearchResult(payload: unknown): {
  items: CityAccommodation[];
  total: number;
  zoneId: number | null;
} {
  const root = payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : null;
  const isSuccess = root?.isSuccess ?? root?.data != null;
  if (!isSuccess) {
    return { items: [], total: 0, zoneId: null };
  }

  const data = root?.data;
  const items: CityAccommodation[] = Array.isArray(data)
    ? (data as CityAccommodation[])
    : data && typeof data === 'object'
      ? (((data as Record<string, unknown>).items as CityAccommodation[]) ??
        ((data as Record<string, unknown>).accommodations as CityAccommodation[]) ??
        [])
      : [];
  const total =
    data && typeof data === 'object'
      ? Number((data as Record<string, unknown>).totalCount ?? items.length) || items.length
      : items.length;
  const zoneId = items[0]?.zoneID ?? null;

  return { items, total, zoneId };
}
